import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Loader } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const T = {
  bg: '#FAFBFF', surface: '#FFFFFF', surface1: '#F0F1FA',
  primary: '#0052CC', primaryLight: '#D6E3FF', onPrimary: '#FFFFFF',
  onSurface: '#1A1C1E', onSurfaceVar: '#44474F',
  outline: '#74777F', outlineVar: '#C4C6D0',
  error: '#BA1A1A', success: '#006D3B',
};

function useApi(token) {
  return useMemo(() => ({
    get: (path) => fetch(`${API}/api${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
  }), [token]);
}

export default function SyncLogsPage({ token: tokenProp }) {
  const token = tokenProp || localStorage.getItem('session_token') || '';
  const api = useApi(token);
  const [olts, setOlts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterOlt, setFilterOlt] = useState('');
  const LIMIT = 50;

  const loadOlts = useCallback(async () => {
    const data = await api.get('/noc/olts');
    setOlts(Array.isArray(data) ? data : []);
  }, [api]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filterOlt) params.set('olt_id', filterOlt);
      // Try new full-schema endpoint first, fall back to old
      let data;
      try {
        data = await api.get(`/noc/sync-logs-v2?${params}`);
      } catch(e2) {
        data = await api.get(`/noc/sync-logs?${params}`);
      }
      setLogs(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total || 0);
    } catch(e) {
      console.error('Failed to load sync logs:', e);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [api, page, filterOlt]);

  useEffect(() => { loadOlts(); }, [loadOlts]);
  useEffect(() => { setPage(1); }, [filterOlt]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: '100vh', fontFamily: 'Roboto, Inter, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: T.onSurface, margin: 0, fontSize: 20, fontWeight: 700 }}>Sync Logs</h1>
          <p style={{ color: T.onSurfaceVar, margin: '4px 0 0', fontSize: 13 }}>OLT synchronization history and results</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={filterOlt} onChange={e => setFilterOlt(e.target.value)}
            style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '9px 12px', color: T.onSurface, fontSize: 13 }}>
            <option value="">All OLTs</option>
            {olts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button onClick={loadLogs} style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 20, padding: '9px 16px', color: T.onSurfaceVar, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: T.surface1 }}>
              {['Time','OLT','Type','Method','Status','ONUs Found','Added','Updated','Duration','Message'].map(h => (
                <th key={h} style={{ padding: '11px 14px', color: T.onSurfaceVar, fontSize: 11, fontWeight: 700, textAlign: 'left', borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: T.onSurfaceVar }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: T.primary }} />
              </td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 60, color: T.onSurfaceVar, fontSize: 14 }}>
                No sync logs yet. Sync an OLT from the OLT Management page.
              </td></tr>
            ) : logs.map(log => (
              <tr key={log.id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}
                onMouseEnter={e => e.currentTarget.style.background = T.surface1}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '10px 14px', color: T.outline, fontSize: 12 }}>{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</td>
                <td style={{ padding: '10px 14px', color: T.onSurface, fontSize: 13, fontWeight: 500 }}>{log.olt_name}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    background: log.sync_type === 'auto' ? '#EDE7F6' : T.primaryLight,
                    color: log.sync_type === 'auto' ? '#6D3B9E' : T.primary,
                    borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600
                  }}>{log.sync_type || 'manual'}</span>
                </td>
                <td style={{ padding: '10px 14px', color: T.onSurfaceVar, fontSize: 13, textTransform: 'uppercase' }}>{log.method}</td>
                <td style={{ padding: '10px 14px' }}>
                  {log.status === 'success' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: T.success, fontSize: 13, fontWeight: 600 }}>
                      <CheckCircle size={13} /> success
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: T.error, fontSize: 13, fontWeight: 600 }}>
                      <AlertCircle size={13} /> error
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 14px', color: T.onSurfaceVar, fontSize: 13, textAlign: 'center' }}>{log.onus_found ?? '—'}</td>
                <td style={{ padding: '10px 14px', color: T.success, fontSize: 13, textAlign: 'center', fontWeight: 600 }}>{log.onus_added ?? '—'}</td>
                <td style={{ padding: '10px 14px', color: T.primary, fontSize: 13, textAlign: 'center', fontWeight: 600 }}>{log.onus_updated ?? '—'}</td>
                <td style={{ padding: '10px 14px', color: T.outline, fontSize: 12 }}>{log.duration_ms ? `${log.duration_ms}ms` : '—'}</td>
                <td style={{ padding: '10px 14px', color: T.error, fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.message || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ color: T.onSurfaceVar, fontSize: 13 }}>Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)} of {total}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '6px 12px', color: T.onSurfaceVar, cursor: page===1?'not-allowed':'pointer' }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ background: T.primary, borderRadius: 8, padding: '6px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '6px 12px', color: T.onSurfaceVar, cursor: page===totalPages?'not-allowed':'pointer' }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
